import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Bot, Cpu, GitBranch, Layers, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const FEATURES = [
  { icon: Bot, title: "Agent-driven", desc: "LangGraph-style tool-calling LLM that plans, explains and adjusts every step." },
  { icon: BarChart3, title: "Full EDA", desc: "Histograms, correlation heatmap, missing-value matrix generated in seconds." },
  { icon: Layers, title: "6+ algorithms", desc: "Linear, RF, ET, GBM, XGBoost, LightGBM — trained, ranked and cross-validated." },
  { icon: Cpu, title: "Auto-preprocessing", desc: "Imputation, encoding and scaling chosen from your data profile." },
  { icon: GitBranch, title: "Deployable pipeline", desc: "Downloadable joblib bundling preprocess + model for one-line inference." },
  { icon: Zap, title: "Live progress", desc: "Streamed training log with a monospace terminal feel." },
];

export default function Landing() {
  const { user } = useAuth();
  const primaryTo = user ? "/dashboard" : "/register";
  const primaryLabel = user ? "Open workspace" : "Get started";
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between h-14 px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-7 h-7 rounded-sm bg-accent text-accent-foreground grid place-items-center">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <span className="font-display font-bold text-lg">MLForge</span>
          </Link>
          <nav className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard" data-testid="landing-dashboard-btn">
                <Button className="text-sm bg-accent text-accent-foreground hover:bg-accent/90">
                  {user.username} · Dashboard <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login-btn">
                  <Button variant="ghost" className="text-sm">Sign in</Button>
                </Link>
                <Link to="/register" data-testid="landing-register-btn">
                  <Button className="text-sm bg-accent text-accent-foreground hover:bg-accent/90">
                    Get started <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-lines pointer-events-none" />
        <div className="absolute inset-0 scan pointer-events-none opacity-40" />
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(45 93% 47% / 0.35), transparent 60%)" }}
        />
        <div className="max-w-[1400px] mx-auto px-6 py-24 md:py-32 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-border rounded-full px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              <span className="label-mono">v1 · SUPERVISED LEARNING</span>
            </div>
            <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02]">
              The AI-powered<br />
              <span className="text-accent">ML Engineer</span> that ships models
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Upload a dataset. MLForge profiles it, plans the pipeline, trains and compares six algorithms,
              tunes the winner and hands you a deployable <span className="font-mono text-foreground">pipeline.joblib</span>.
              Every decision explained by the agent.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to={primaryTo} data-testid="hero-cta">
                <Button className="h-11 px-6 text-sm bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm">
                  {primaryLabel} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to={user ? "/projects/new" : "/login"}>
                <Button variant="outline" className="h-11 px-6 text-sm rounded-sm border-border hover:bg-secondary">
                  {user ? "New run" : "Sign in"}
                </Button>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                ["6+", "Algorithms"],
                ["3-fold", "Cross-val"],
                ["<60s", "MVP run"],
                [".joblib", "Export"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display font-bold text-3xl num text-foreground">{n}</div>
                  <div className="label-mono mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="label-mono mb-3">// CAPABILITIES</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
              A full ML workflow — automated end to end.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-8 border-r border-b border-border hover:bg-secondary/40 transition-colors">
                <Icon className="w-5 h-5 text-accent mb-4" strokeWidth={1.5} />
                <div className="font-display font-semibold text-lg mb-1">{title}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-border bg-secondary/20">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="label-mono mb-3">// PIPELINE</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-12">
            Upload → Analyse → Train → Deploy
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              ["01", "Ingest", "CSV / Excel / Parquet, drag-and-drop."],
              ["02", "Profile", "Types, missing, duplicates, target auto-detect."],
              ["03", "Train", "6 estimators + holdout + 3-fold CV."],
              ["04", "Ship", "Joblib pipeline, HTML report, chat explanations."],
            ].map(([n, t, d]) => (
              <div key={n} className="border border-border p-6 bg-card hover:border-accent/40 transition-colors">
                <div className="label-mono text-accent">{n}</div>
                <div className="font-display font-semibold text-xl mt-3">{t}</div>
                <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 py-8 flex items-center justify-between text-xs text-muted-foreground">
          <div>© MLForge · Automated ML workflows</div>
          <div className="font-mono">v1.0 · openai-compatible</div>
        </div>
      </footer>
    </div>
  );
}
